import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import Button from "core/buttons/button/Button";
import Icon from "core/icon/Icon";
import { Typography } from "core/typography/Typography";
import {
  DELIVERY_NOTE_DEFAULT_COLUMN_NAMES,
  DELIVERY_NOTE_GENERATE_LABELS,
  RULES,
} from "constants/forms";
import {
  buildDeliveryNoteLineHeaders,
  computeLineAmount,
  formatDeliveryNoteAmount,
  readLineCell,
} from "utils/deliveryNoteHelper";

const DeliveryNoteLinesBlock = ({
  columns,
  lines,
  currencySymbol,
  onAddLine,
  onRemoveLine,
  onCellChange,
}) => {
  const intl = useIntl();
  const headers = buildDeliveryNoteLineHeaders(columns);
  const removeLabel = intl.formatMessage(
    DELIVERY_NOTE_GENERATE_LABELS.removeLine
  );

  const formatAmount = (amount) =>
    formatDeliveryNoteAmount({ intl, amount, currencySymbol });

  const handleChange = (lineIndex, header) => (event) => {
    const { value } = event.target;
    if (header.field !== "value" && value && !RULES.amount_input.test(value))
      return;
    onCellChange({
      lineIndex,
      position: header.position,
      field: header.field,
      value,
    });
  };

  const cellValue = (line, header) =>
    readLineCell(line, header.position)[header.field] ?? "";

  return (
    <section className="delivery-note-lines" id="delivery-note-lines">
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={
          <FormattedMessage {...DELIVERY_NOTE_GENERATE_LABELS.linesBlock} />
        }
      />

      <table className="delivery-note-lines__table">
        <thead>
          <tr>
            {headers.map((header) => (
              <th
                key={`header-${header.position}-${header.field}`}
                className={
                  header.field === "total" ? "delivery-note-lines__total" : ""
                }
              >
                {header.field === "total" ? (
                  <FormattedMessage
                    {...DELIVERY_NOTE_DEFAULT_COLUMN_NAMES.total}
                  />
                ) : (
                  header.label
                )}
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.map((line, lineIndex) => (
            <tr key={line.key}>
              {headers.map((header) =>
                header.field === "total" ? (
                  <td
                    key={`cell-${header.position}-total`}
                    className="delivery-note-lines__total"
                    data-testid={`delivery-note-line-${lineIndex}-amount-${header.position}`}
                  >
                    {formatAmount(
                      computeLineAmount({
                        column: columns[header.position],
                        cell: readLineCell(line, header.position),
                      })
                    )}
                  </td>
                ) : (
                  <td key={`cell-${header.position}-${header.field}`}>
                    <input
                      type="text"
                      className="delivery-note-lines__input"
                      value={cellValue(line, header)}
                      onChange={handleChange(lineIndex, header)}
                      data-testid={`delivery-note-line-${lineIndex}-cell-${header.position}-${header.field}`}
                    />
                  </td>
                )
              )}
              <td className="delivery-note-lines__remove">
                <button
                  type="button"
                  className="delivery-note-lines__action"
                  onClick={() => onRemoveLine(lineIndex)}
                  aria-label={removeLabel}
                  title={removeLabel}
                  data-testid={`delivery-note-line-${lineIndex}-delete`}
                >
                  <Icon icon="trash-2" color="red" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Button
        type="button"
        className="btn-small btn-outline"
        onClick={onAddLine}
        id="delivery-note-add-line"
      >
        <FormattedMessage {...DELIVERY_NOTE_GENERATE_LABELS.addLine} />
      </Button>
    </section>
  );
};

DeliveryNoteLinesBlock.propTypes = {
  columns: PropTypes.array,
  lines: PropTypes.array.isRequired,
  currencySymbol: PropTypes.string,
  onAddLine: PropTypes.func.isRequired,
  onRemoveLine: PropTypes.func.isRequired,
  onCellChange: PropTypes.func.isRequired,
};

export default DeliveryNoteLinesBlock;
